import { useState, useEffect } from 'react';
import { Plus, RefreshCw, LogIn, LogOut, Camera, Car, Clock, CheckCircle } from 'lucide-react';
import api from '../api/api';

const CAM_STATUS = {
  active: { l: 'Activa', cls: 'bg-green-100 text-green-700' },
  fault: { l: 'Avería', cls: 'bg-red-100 text-red-700' },
  offline: { l: 'Offline', cls: 'bg-gray-100 text-gray-500' },
};

export default function Accesos() {
  const [tab, setTab] = useState('accesos');
  const [logs, setLogs] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ vehicle_plate: '', driver_name: '', company: '', purpose: '', entry_time: new Date().toISOString().slice(0, 16), notes: '' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [logsRes, camRes] = await Promise.all([api.get('/security/access?limit=100'), api.get('/security/cameras')]);
      setLogs(logsRes.data);
      setCameras(camRes.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleRegisterEntry = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/security/access', form);
      setShowModal(false); loadAll();
    } finally { setSaving(false); }
  };

  const handleLogExit = async (id) => {
    await api.put(`/security/access/${id}/exit`, {});
    loadAll();
  };

  const activeInside = logs.filter(l => !l.exit_time).length;

  const formatTime = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Control de Accesos</h1>
          <p className="text-sm text-gray-500 mt-0.5">Registro de entradas/salidas y videovigilancia</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"><RefreshCw size={16} /></button>
          {tab === 'accesos' && <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="register-entry-btn"><Plus size={16} /> Registrar Entrada</button>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { icon: Car, l: 'Accesos hoy', v: logs.filter(l => l.entry_time?.startsWith(new Date().toISOString().slice(0, 10))).length, c: '#6b7280' },
          { icon: LogIn, l: 'En instalación', v: activeInside, c: '#16a34a' },
          { icon: Camera, l: 'Cámaras activas', v: cameras.filter(c => c.status === 'active').length, c: '#3b82f6' },
          { icon: CheckCircle, l: 'Cámaras con avería', v: cameras.filter(c => c.status === 'fault').length, c: '#ef4444' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.l} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: s.c + '15' }}><Icon size={18} style={{ color: s.c }} /></div>
              <div><p className="text-xs text-gray-500">{s.l}</p><p className="text-xl font-bold" style={{ fontFamily: 'Barlow, sans-serif', color: s.c }}>{s.v}</p></div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[{ k: 'accesos', l: `Registro de Accesos (${logs.length})` }, { k: 'camaras', l: `Cámaras (${cameras.length})` }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? 'border-[#16a34a] text-[#16a34a]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t.l}</button>
        ))}
      </div>

      {tab === 'accesos' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" /></div> : (
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                {['Matrícula', 'Conductor', 'Empresa', 'Motivo', 'Entrada', 'Salida', 'Estado', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {logs.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400 text-sm">Sin registros de acceso</td></tr>
                  : logs.map(log => (
                    <tr key={log.id} className="table-row-hover" data-testid={`access-row-${log.id}`}>
                      <td className="px-3 py-2.5 font-mono font-bold text-gray-900 text-xs">{log.vehicle_plate}</td>
                      <td className="px-3 py-2.5 text-sm text-gray-700">{log.driver_name || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{log.company || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 max-w-xs truncate">{log.purpose || '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600 whitespace-nowrap"><div className="flex items-center gap-1"><LogIn size={12} className="text-green-600" />{formatTime(log.entry_time)}</div></td>
                      <td className="px-3 py-2.5 font-mono text-xs text-gray-600 whitespace-nowrap">
                        {log.exit_time ? <div className="flex items-center gap-1"><LogOut size={12} className="text-red-500" />{formatTime(log.exit_time)}</div> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {log.exit_time
                          ? <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full"><CheckCircle size={11} />Salido</span>
                          : <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><Clock size={11} />En planta</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {!log.exit_time && <button onClick={() => handleLogExit(log.id)} className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 border border-red-200 whitespace-nowrap" data-testid={`log-exit-${log.id}`}>Registrar salida</button>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'camaras' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cameras.length === 0 ? <div className="col-span-3 bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">Sin cámaras configuradas</div>
            : cameras.map(cam => (
              <div key={cam.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid={`camera-card-${cam.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-gray-900 rounded-lg"><Camera size={20} className="text-white" /></div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CAM_STATUS[cam.status]?.cls || 'bg-gray-100 text-gray-500'}`}>{CAM_STATUS[cam.status]?.l || cam.status}</span>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-0.5">{cam.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{cam.location}</p>
                {cam.ip_address && <p className="text-xs font-mono text-gray-400">IP: {cam.ip_address}</p>}
                {cam.notes && <p className="text-xs text-gray-400 mt-2 italic">{cam.notes}</p>}
              </div>
            ))}
        </div>
      )}

      {/* Entry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Registrar Entrada</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleRegisterEntry} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Matrícula *</label>
                  <input type="text" required value={form.vehicle_plate} onChange={e => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })} placeholder="1234-ABC" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none font-mono uppercase" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Conductor</label>
                  <input type="text" value={form.driver_name} onChange={e => setForm({ ...form, driver_name: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Empresa</label>
                  <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Hora entrada</label>
                  <input type="datetime-local" value={form.entry_time} onChange={e => setForm({ ...form, entry_time: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              </div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Motivo / Propósito</label>
                <input type="text" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="Descarga RSU, Mantenimiento externo..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-entry-btn">{saving ? 'Registrando...' : 'Registrar Entrada'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
